<template>
  <v-row no-gutters>
    <v-col cols="12" class="mb-4">
      <h3 class="header__title--main">
        {{ $t('message.pages.analytics.references') }}
      </h3>
    </v-col>
    <v-card v-if="noInfoFound" width="1000px" height="300px" flat>
      <empty-state />
    </v-card>
    <v-row no-gutters justify="center" v-else>
      <v-card
        flat
        v-if="loading"
        class="d-flex align-center justify-center px-3"
        height="273px"
        min-width="866px"
      >
        <loading-logo :value="200" />
      </v-card>
      <v-col cols="12" v-else>
        <v-row
          v-if="errors.fetchError"
          class="flex-column"
          justify="center"
          align="center"
          no-gutters
          style="min-height: 250px"
        >
          <v-card flat height="30" class="mb-4">
            <span class="footerText align-text d-block">
              {{ errors.shownErrMsg }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon size="18" v-bind="attrs" v-on="on">
                    mdi-help-circle
                  </v-icon>
                </template>
                <v-container class="text-body-2 pa-0" style="max-width: 230px">
                  {{ errors.tooltipErrMsg }}
                </v-container>
              </v-tooltip>
            </span>
          </v-card>
          <v-btn color="primary" class="ml-3" @click="fetchData">{{
            $t('message.pages.analytics.tryAgain')
          }}</v-btn>
        </v-row>
        <v-row v-else no-gutters align="center">
          <v-col cols="12" class="pr-2">
            <apexchart
              :class="{ darkThemeSvgFix: $vuetify.theme.dark }"
              type="area"
              height="250"
              :options="viewsChartOptions"
              :series="viewChartSeries"
            ></apexchart>
          </v-col>
        </v-row>
      </v-col>
      <v-col v-if="loading" cols="12" class="mt-14">
        <v-skeleton-loader type="table-tbody" />
      </v-col>
      <template v-else-if="!errors.fetchError">
        <v-col cols="12" class="mt-14 px-4">
          <v-card height="48px" width="100%" flat class="d-flex align-center">
            <v-row no-gutters style="font-size: 12px; font-weight: bold">
              <v-col cols="4">
                <span style="padding-left: 36px">URL</span>
              </v-col>
              <v-col cols="2" class="d-flex justify-end">
                <span>{{ $t('message.pages.analytics.account.views') }}</span>
              </v-col>
              <v-col cols="2" class="d-flex justify-end">
                <span>{{
                  $t('message.pages.analytics.account.uniqueViews')
                }}</span>
              </v-col>
              <v-col cols="2" class="d-flex justify-end">
                <span>{{ $t('message.pages.analytics.account.plays') }}</span>
              </v-col>
              <v-col cols="2" class="d-flex justify-end pr-2">
                <span>{{
                  $t('message.pages.analytics.account.uniquePlays')
                }}</span>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          class="px-4 mb-2"
          style="overflow-y: auto; overflow-x: hidden"
          :style="`height: ${tableHeight}`"
        >
          <v-card
            v-for="(deviceData, i) in allReferencesData"
            height="48px"
            width="100%"
            flat
            style="border-top: 1px solid #e6e8f8; border-radius: 0px"
            class="d-flex align-center data-table__item"
            :key="i"
            @click="setChartReferenceItem(deviceData)"
          >
            <v-row no-gutters style="font-size: 13px">
              <v-col cols="4">
                <v-row no-gutters align="center">
                  <v-icon
                    class="mx-2"
                    color="primary"
                    size="20"
                    v-if="deviceData.name === shownChartVideo.name"
                  >
                    mdi-check
                  </v-icon>
                  <span
                    :title="deviceData.name"
                    class="d-inline-block text-truncate"
                    style="max-width: 90%"
                    :style="
                      deviceData.name != shownChartVideo.name
                        ? 'padding-left: 36px'
                        : ''
                    "
                  >
                    {{ deviceData.name ? deviceData.name : '-' }}
                  </span>
                </v-row>
              </v-col>
              <v-col cols="2" class="d-flex justify-end">
                <span>{{ Number(deviceData.view).toLocaleString() }}</span>
              </v-col>
              <v-col cols="2" class="d-flex justify-end">
                <span>
                  {{ Number(deviceData.unique_view).toLocaleString() }}
                </span>
              </v-col>
              <v-col cols="2" class="d-flex justify-end">
                <span>{{ Number(deviceData.play).toLocaleString() }}</span>
              </v-col>
              <v-col cols="2" class="d-flex justify-end pr-2">
                <span>{{
                  Number(deviceData.unique_play).toLocaleString()
                }}</span>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </template>
    </v-row>
  </v-row>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import newAnalyticsApi from '@/services/newAnalyticsApi'
import { format } from 'date-fns'
import LoadingLogo from '@/components/LoadingLogo.vue'
import i18n from '@/translations/lang'
import EmptyState from './EmptyState.vue'

export default {
  name: 'References',
  components: {
    LoadingLogo,
    apexchart: VueApexCharts,
    EmptyState,
  },
  props: {
    componentData: {
      type: Object,
      default: () => ({ selectedVideos: [], dateRange: [] }),
      required: true,
    },
  },
  data: () => ({
    loading: false,
    referencesViews: [],
    allReferencesData: [],
    noInfoFound: false,
    viewsChartOptions: {
      colors: ['#5bc6ac'],
      legend: {
        show: false,
      },
      chart: {
        toolbar: false,
        type: 'area',
        height: 350,
        width: '100%',
        offsetX: 0,
        offsetY: 0,
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        type: 'datetime',
        categories: [],
        labels: {
          format: 'dd/MM',
          datetimeUTC: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      tooltip: {
        theme: 'dark',
        x: {
          format: 'dd/MM/yy HH:mm',
        },
        y: {
          formatter: function (value, { seriesIndex, dataPointIndex, series }) {
            if (seriesIndex === 3 && value >= 0) {
              // verifica se o "Y" atual é o de plays únicos
              const uniquePlays = value
              const uniqueViews = series[1][dataPointIndex]
              let playRate = 0
              if (uniquePlays && uniqueViews) {
                playRate = ((uniquePlays * 100) / uniqueViews).toFixed(1)
              }
              return `${value} (${playRate}%)`
            } else if (seriesIndex === 2 && value >= 0) {
              // verifica se o "Y" atual é o de plays
              const plays = value
              const views = series[0][dataPointIndex]
              let playRate = 0
              if (plays && views) {
                playRate = ((plays * 100) / views).toFixed(1)
              }
              return `${value} (${playRate}%)`
            }
            return value
          },
        },
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 0.95,
          opacityFrom: 0.7,
          opacityTo: 0.65,
          stops: [0, 98, 100],
        },
      },
    },
    viewChartSeries: [],
    shownChartVideo: {
      name: '',
    },
    loadedReferencesData: {},
    errors: {
      fetchError: false,
      shownErrMsg: '',
      tooltipErrMsg: '',
    },
  }),
  computed: {
    tableHeight() {
      const length = this.allReferencesData.length
      if (length < 5) {
        return 267
      } else {
        return length * 48 + 5
      }
    },
    selectedVideos() {
      const { selectedVideos } = this.componentData
      return selectedVideos
    },
    dateRange() {
      const { dateRange } = this.componentData
      return dateRange
    },
  },
  watch: {
    componentData: {
      handler() {
        this.fetchData()
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    async fetchData() {
      this.allReferencesData = []
      this.referencesViews = []
      this.noInfoFound = false
      this.loading = true
      await this.getReferencesInfo()
      this.loading = false
    },
    async getSelectedReferencesInfo() {
      let referencesResponses = []
      let referencesData = {}
      for (let video of this.selectedVideos) {
        let ret = await newAnalyticsApi.getSingleReferencesMetrics(
          video.id,
          format(this.dateRange[0], 'yyyy-MM-dd'),
          format(this.dateRange[1], 'yyyy-MM-dd')
        )
        referencesResponses.push(ret.data)
      }
      referencesResponses.forEach((reference) => {
        let eachReferenceKey = Object.keys(reference)
        eachReferenceKey.forEach((key) => {
          if (key == undefined || key === 'undefined') return
          let referenceKey = referencesData[`${key}`]
          if (!referenceKey) referencesData[`${key}`] = {}
          let browserInfo = reference[key]
          let eachBrowserDateArray = Object.keys(browserInfo)
          eachBrowserDateArray.forEach((day) => {
            let checkIfObjectHasDay = referencesData[key][day]
            if (!checkIfObjectHasDay)
              referencesData[key][day] = {
                play: 0,
                view: 0,
                unique_play: 0,
                unique_view: 0,
              }
            referencesData[key][day]['play'] += reference[key][day]['play'] ?? 0
            referencesData[key][day]['unique_play'] +=
              reference[key][day]['unique_play'] ?? 0
            referencesData[key][day]['unique_view'] +=
              reference[key][day]['unique_view'] ?? 0
            referencesData[key][day]['view'] += reference[key][day]['view'] ?? 0
          })
        })
      })
      this.setReferencesData(referencesData)
    },
    async getReferencesInfo() {
      if (this.selectedVideos.length) {
        await this.getSelectedReferencesInfo()
        return
      }
      try {
        const ret = await newAnalyticsApi.getReferencesMetrics(
          format(this.dateRange[0], 'yyyy-MM-dd'),
          format(this.dateRange[1], 'yyyy-MM-dd')
        )
        if (!Object.keys(ret.data).length) {
          this.noInfoFound = true
          return
        }
        this.setReferencesData(ret.data)
      } catch (e) {
        this.handleFetchError(e)
      }
    },
    setReferencesData(data) {
      let referencesData = data
      this.loadedReferencesData = data
      let allReferencesDataAggregated = {}
      for (let deviceData in referencesData) {
        let deviceObject = {
          view: 0,
          play: 0,
          unique_view: 0,
          unique_play: 0,
        }
        for (let day in referencesData[`${deviceData}`]) {
          let eachDay = referencesData[`${deviceData}`][`${day}`]
          deviceObject.view += eachDay.view ?? 0
          deviceObject.play += eachDay.play ?? 0
          deviceObject.unique_view += eachDay.unique_view ?? 0
          deviceObject.unique_play += eachDay.unique_play ?? 0
        }
        allReferencesDataAggregated[`${deviceData}`] = deviceObject
      }
      for (let device in allReferencesDataAggregated) {
        this.allReferencesData.push({
          name: device,
          ...allReferencesDataAggregated[`${device}`],
        })
      }
      this.allReferencesData.sort((a, b) => b.view - a.view)
      this.setChartReferenceItem(this.allReferencesData[0])
      let mostViewed = this.allReferencesData[0]
      let chartItem = {
        name: mostViewed.name,
        viewsData: referencesData[`${mostViewed.name}`],
      }
      this.fixAnalyticsChart(chartItem)
    },
    setChartReferenceItem(item) {
      let chartItem = {
        name: item.name,
        viewsData: this.loadedReferencesData[`${item.name}`],
      }
      this.fixAnalyticsChart(chartItem)
    },
    async fixAnalyticsChart(video) {
      this.getDateDiff(
        format(this.dateRange[0], 'yyyy-MM-dd'),
        format(this.dateRange[1], 'yyyy-MM-dd'),
        video
      )
      this.shownChartVideo = video
      let videoChartData = [
        {
          name: i18n.t('message.pages.video.metricsPage.views'),
          data: Object.keys(video.viewsData)
            .map((date) => ({
              x: new Date(date.replace(/-/gm, '/')).toISOString(),
              y: video.viewsData[`${date}`].view,
            }))
            .sort(function (a, b) {
              return new Date(b.x) - new Date(a.x)
            }),
        },
      ]
      this.viewChartSeries = videoChartData
    },
    getDateDiff(startDate, endDate, video) {
      let start = new Date(startDate)
      let end = new Date(endDate)

      while (start <= end) {
        if (!video.viewsData[new Date(start).toISOString().split('T')[0]]) {
          video.viewsData[new Date(start).toISOString().split('T')[0]] = {
            play: 0,
            unique_play: 0,
            unique_view: 0,
            view: 0,
          }
        }
        start.setDate(start.getDate() + 1)
      }
    },
    handleFetchError(err) {
      this.errors.fetchError = true
      this.errors.tooltipErrMsg =
        err.msg ??
        err.response.data.errMsg ??
        err.response.data.message ??
        'Error'
      this.errors.shownErrMsg = i18n.t('message.errors.unexpectedError')
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => {
        if (typeof this.errors[e] === 'string') {
          return (this.errors[e] = '')
        }
        this.errors[e] = false
      })
    },
  },
}
</script>
